import { useMemo, useContext, useCallback, createContext } from 'react';

import { codeNameToJourneyStep } from 'utils';
import {
  CourseStep,
  VaccineStep,
  CourseWithSteps,
  CourseStepStatus,
  InternalCourseStep,
  CourseStepSubmission,
  CourseSubmissionStatus,
} from 'types';

import { useTranslationContext } from '../translation';

import { CourseContextProps, CourseContextProviderProps } from './types';

const CourseContext = createContext<CourseContextProps>({
  language: 'en',
  isLoaded: false,
  isLoading: true,
  hasCourse: false,
  vaccineSteps: [],
  isFinished: false,
  showWelcome: true,
  getStep: () => undefined,
  getInternalStep: () => undefined,
  getStepStatus: () => CourseStepStatus.Locked,
});

export const CourseContextProvider = ({
  steps,
  course,
  children,
  isLoaded,
  isLoading,
  enrollment,
  submission,
  submissions,
}: CourseContextProviderProps) => {
  const { language } = useTranslationContext();
  const stepsMap = useMemo(() => {
    const map = new Map<string, CourseStep>();

    steps?.forEach((step) => {
      map.set(step.id, step);
    });

    return map;
  }, [steps]);

  const isFinished = submission?.status === CourseSubmissionStatus.COMPLETED;

  const getStep = useCallback(
    (stepId: string) => stepsMap.get(stepId),
    [stepsMap]
  );

  const showWelcome = !enrollment;

  const { hasCourse, courseWithSteps } = useMemo(() => {
    const hasCourse = !!course && !!steps;

    const courseWithSteps: CourseWithSteps | undefined = hasCourse
      ? {
          ...course,
          steps: course.stepIds
            .map((id) => {
              return steps.find((step) => step.id === id);
            })
            .filter(Boolean) as CourseStep[],
        }
      : undefined;

    if (
      courseWithSteps &&
      courseWithSteps.steps.length !== course?.stepIds.length
    ) {
      return {
        hasCourse: false,
        courseWithSteps: undefined,
      };
    }

    return { hasCourse, courseWithSteps };
  }, [course, steps]);

  const { statuses, vaccineSteps, internalSteps } = useMemo(() => {
    const statuses = new Map<string, CourseStepStatus>();
    const internalSteps: InternalCourseStep[] = [];
    const vaccineSteps: VaccineStep[] = [];

    if (!courseWithSteps || !submissions) {
      return { statuses, vaccineSteps, internalSteps };
    }

    const submissionsMap = new Map<string, CourseStepSubmission>();
    submissions?.forEach((submission) => {
      submissionsMap.set(submission.courseStepId, submission);
    });

    let foundFirstActive = false;

    for (const step of courseWithSteps.steps) {
      const submission = submissionsMap.get(step.id);

      if (submission?.isSubmitted) {
        statuses.set(step.id, CourseStepStatus.Completed);
      } else if (!foundFirstActive) {
        statuses.set(step.id, CourseStepStatus.Active);
        foundFirstActive = true;
      } else {
        statuses.set(step.id, CourseStepStatus.Locked);
      }

      const type = codeNameToJourneyStep(step.codeName);

      const found = internalSteps.find((item) => item.type === type);
      if (found) {
        found.steps.push(step);
      } else {
        internalSteps.push({
          type,
          progress: 0,
          steps: [step],
          presentStep: step,
          status: CourseStepStatus.Locked,
        });
      }
    }

    for (const internalStep of internalSteps) {
      const totalSteps = internalStep.steps.length;
      let completedSteps = 0;
      let activeStepIndex = -1;

      for (let i = 0; i < internalStep.steps.length; i++) {
        const step = internalStep.steps[i];
        const status = statuses.get(step.id);

        if (step.id === internalStep.presentStep.id) {
          if (status === CourseStepStatus.Completed) {
            internalStep.status = CourseStepStatus.Completed;
          } else if (status === CourseStepStatus.Active) {
            internalStep.status = CourseStepStatus.Active;
          } else {
            internalStep.status = CourseStepStatus.Locked;
          }
        }

        if (status === CourseStepStatus.Completed) {
          completedSteps++;
        } else if (status === CourseStepStatus.Active) {
          internalStep.presentStep = step;
          activeStepIndex = i;
        }
      }

      if (
        activeStepIndex !== -1 &&
        activeStepIndex + 1 < internalStep.steps.length
      ) {
        internalStep.nextStep = internalStep.steps[activeStepIndex + 1];
      } else if (completedSteps === totalSteps) {
        internalStep.nextStep = undefined;
      } else {
        internalStep.nextStep =
          internalStep.steps.find((step) => {
            const status = statuses.get(step.id);
            return status === CourseStepStatus.Locked;
          }) || undefined;
      }

      const percent = Math.round((completedSteps / totalSteps) * 100);
      // TODO: maybe cal progress based on submission for active step
      internalStep.progress = percent;
    }

    return { statuses, vaccineSteps, internalSteps };
  }, [courseWithSteps, submissions]);

  const getStepStatus = useCallback(
    (stepId: string) => {
      return statuses.get(stepId) || CourseStepStatus.Locked;
    },
    [statuses]
  );

  const getInternalStep = useCallback(
    (stepId: string) => {
      for (const step of internalSteps) {
        const stepIds = new Set(step.steps.map(({ id }) => id));
        if (stepIds.has(stepId)) {
          return step;
        }
      }
      return undefined;
    },
    [internalSteps]
  );

  return (
    <CourseContext.Provider
      value={{
        getStep,
        isLoaded,
        language,
        hasCourse,
        isLoading,
        isFinished,
        submission,
        showWelcome,
        vaccineSteps,
        getStepStatus,
        internalSteps,
        getInternalStep,
        rawCourse: course,
        course: courseWithSteps,
        submissionId: submission?.id,
        steps: courseWithSteps?.steps,
      }}
    >
      {children}
    </CourseContext.Provider>
  );
};

export const useCourseContext = () => {
  return useContext(CourseContext);
};
